import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import type { ViewId } from './contract.js';

/**
 * VIEW BOUNDARY : le filet sous les vues.
 *
 * Une vue qui plante ne doit jamais emporter l'ecran avec elle. Ici, la vue
 * fautive est demontee et le core retombe automatiquement sur la vue de repli
 * (par defaut `core.liste`, la vue universelle : elle n'exige qu'un titre).
 * L'avertissement part dans la console avec l'id de la vue : c'est le seul
 * endroit ou l'indirection "descripteur + hote + projection" redevient lisible
 * au debug.
 *
 * Deux cas distincts :
 * - la vue fautive n'est PAS la vue de repli : on bascule, l'utilisateur garde
 *   son ecran, ses filtres et sa recherche ;
 * - la vue fautive EST la vue de repli : on ne boucle pas, on rend un message
 *   sobre a la place.
 *
 * Le boundary se REARME quand `viewId` change (l'utilisateur retente la vue).
 */

/** Vue de repli par defaut : celle qui n'exige qu'un titre. */
export const FALLBACK_VIEW_ID: ViewId = 'core.liste';

export interface ViewBoundaryProps {
  /** La vue actuellement montee (sert de cle de rearmement). */
  viewId: ViewId;
  /** Vers quoi retomber. Defaut `core.liste`. */
  fallbackViewId?: ViewId;
  /** Demande de bascule, honoree par le core (`useScreen().setView`). */
  onFallback(id: ViewId): void;
  /** Bloc rendu quand la vue de repli est elle-meme en echec. */
  renderError(message: string): ReactNode;
  children: ReactNode;
}

interface ViewBoundaryState {
  /** L'id de la vue qui a plante, null si tout va bien. */
  failedViewId: ViewId | null;
}

export class ViewBoundary extends Component<ViewBoundaryProps, ViewBoundaryState> {
  override state: ViewBoundaryState = { failedViewId: null };

  static getDerivedStateFromProps(
    props: ViewBoundaryProps,
    state: ViewBoundaryState,
  ): ViewBoundaryState | null {
    // Rearmement : on a change de vue depuis l'echec.
    if (state.failedViewId != null && state.failedViewId !== props.viewId) {
      return { failedViewId: null };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const { viewId, fallbackViewId = FALLBACK_VIEW_ID, onFallback } = this.props;
    console.error(
      `[taki/views] La vue ${viewId} a plante et a ete demontee.`,
      error,
      info.componentStack,
    );
    this.setState({ failedViewId: viewId });
    if (viewId !== fallbackViewId) {
      console.warn(`[taki/views] Repli automatique sur ${fallbackViewId}.`);
      onFallback(fallbackViewId);
    }
  }

  override render(): ReactNode {
    const { viewId, fallbackViewId = FALLBACK_VIEW_ID, renderError, children } = this.props;
    if (this.state.failedViewId === viewId) {
      // La vue de repli est elle-meme en echec : on s'arrete la, pas de boucle.
      if (viewId === fallbackViewId) {
        return renderError(`La vue ${viewId} n'a pas pu s'afficher.`);
      }
      // Bascule demandee : rien a rendre le temps que le core change de vue.
      return null;
    }
    return children;
  }
}
